import { type CSSProperties, useEffect, useRef, useState } from "react";

import Button from "../../../../../../components/atoms/button";
import AssessmentCodeEditor from "./editor/assessmentCodingEditor";

import { useAppDispatch, useAppSelector } from "../../../../../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";

import GetHeroIcon from "../../../../../../utils/getHeroIcons";
import Tooltip from "@assessment/components/atoms/tooltip";
import { Question, sampleTestCase } from "@assessment/types/assessmentOngoing";
import {
  useFlagQuestionMutation,
  useGetSampleTestCasesQuery
} from "@assessment/store/slices/api/assessmentApiSlice";
import { useShowErrors } from "../../../toasterWrapper";
import ShowHint from "../../hint-explanation/showHint";
import QuestionDisplay from "./components/questionDisplay";
import TestCaseListing from "./components/testCaseListing";
import HintAndExplanation from "./components/hintAndExplanation";
import { useTranslation } from "react-i18next";
interface AssessmentCodingQuestions {
  question: Question;
  sectionId: string;
  totalQuestionsInSection: number;
  changeActiveQuestion: (val: "next" | "previous") => void;
}

const AssessmentCodingQuestion = ({
  question,
  sectionId,
  totalQuestionsInSection,
  changeActiveQuestion
}: AssessmentCodingQuestions) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  const ongoingId = useAppSelector((state) => state.AssessmentSlice.ongoingId);

  const [editorTheme, setEditorTheme] = useState<"vs-dark" | "light">(
    "vs-dark"
  );
  const [revealedHintsCount, setRevealedHintsCount] = useState(0);
  const [isHintLoading, setIsHintLoading] = useState(false);

  const [showHintSection, setShowHintSection] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const responses = useAppSelector(
    (store) => store.AssessmentSlice.candidateResponse
  );

  const candidateResponseForQuestion = activeQuestionMeta?.questionId
    ? responses[activeQuestionMeta.questionId!]
    : undefined;

  const { data: testCaseList } = useGetSampleTestCasesQuery(
    {
      ongoingId: ongoingId || "",
      questionId: activeQuestionMeta?.questionId || "",
      questionVersion: question?.versionnumber || ""
    },
    { refetchOnMountOrArgChange: true }
  );
  const [flagQuestion] = useFlagQuestionMutation();
  const showErrors = useShowErrors();

  const sampleTestCases: sampleTestCase[] = testCaseList?.data?.sampleTestCases;

  const saveCodeInStoreHandler = (newVal: string | undefined) => {
    dispatch(
      AssessmentSliceActions.mutateCandidateResponse({
        id: question.id,
        answerKey: newVal
      })
    );
  };

  useEffect(() => {
    if (testCaseList?.data) {
      dispatch(AssessmentSliceActions.mutateTestCases(testCaseList?.data));
    }
  }, [testCaseList]);

  const next = () => {
    if (isLastQuestionInTheSection) {
      dispatch(AssessmentSliceActions.mutateShowSummaryModal(true));
    } else changeActiveQuestion("next");
  };

  const flagQuestionHandler = async () => {
    try {
      setAttemptingQuestion(true);

      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      const res = await flagQuestion({
        flag: questionFlagged ? "false" : "true",
        ongoingId: ongoingId,
        questionId: question.questionId
      });
      if (res.error) {
        throw {
          status: "",
          title: "Error updating the status of the quesiton",
          err: res.error,
          description: ""
        };
      }

      dispatch(
        AssessmentSliceActions.mutateQuestionFlagged({
          questionId: question.questionId,
          sectionId: sectionId,
          value: !questionFlagged
        })
      );
      setAttemptingQuestion(false);
    } catch (err) {
      console.error(err);
      const temp: any = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setAttemptingQuestion(false);
    }
  };

  const isLastQuestionInTheSection =
    question.orderInSection === totalQuestionsInSection;
  const [attemptingQuestion, setAttemptingQuestion] = useState(false);
  const assessmentProgress = useAppSelector(
    (state) => state.AssessmentSlice.assessmentProgress
  );
  const questionFlagged =
    assessmentProgress[sectionId].questions[question.questionId].flagged;

  const editorFullScreen = useAppSelector(
    (store) => store.AssessmentSlice.editorFullScreen
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [availableViewportHeight, setAvailableViewportHeight] = useState<
    number | null
  >(null);
  const [questionWidth, setQuestionWidth] = useState(33.33); // Initial % width of question panel
  const isDragging = useRef(false);

  const startDrag = () => {
    isDragging.current = true;
  };

  const stopDrag = () => {
    isDragging.current = false;
  };

  const onDrag = (e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const offsetLeft = container.getBoundingClientRect().left;
    const mouseX = e.clientX - offsetLeft;

    const newQuestionWidth = (mouseX / containerWidth) * 100;

    // Clamp between 10% and 60%
    if (newQuestionWidth >= 18 && newQuestionWidth <= 60) {
      setQuestionWidth(newQuestionWidth);
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", stopDrag);
    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
    };
  }, []);
  useEffect(() => {
    setShowHintSection(false);
    setRevealedHintsCount(0);
    setIsHintLoading(false);
  }, [question.questionId]);

  useEffect(() => {
    if (editorFullScreen) setQuestionWidth(0);
    else setQuestionWidth(33.33);
  }, [editorFullScreen]);

  useEffect(() => {
    const updateAvailableViewportHeight = () => {
      if (!containerRef.current) return;

      const topOffset = containerRef.current.getBoundingClientRect().top;
      const bottomSpacing = window.innerWidth >= 1024 ? 24 : 16;
      const nextHeight = Math.max(
        window.innerHeight - topOffset - bottomSpacing,
        320
      );

      setAvailableViewportHeight(nextHeight);
    };

    updateAvailableViewportHeight();
    window.addEventListener("resize", updateAvailableViewportHeight);
    window.addEventListener("orientationchange", updateAvailableViewportHeight);

    return () => {
      window.removeEventListener("resize", updateAvailableViewportHeight);
      window.removeEventListener(
        "orientationchange",
        updateAvailableViewportHeight
      );
    };
  }, []);

  const EllipsisRow = () => (
    <div className="flex">
      {[0, 1].map((i) => (
        <GetHeroIcon
          key={i}
          name="EllipsisVerticalIcon"
          variant="outline"
          className={`flex-shrink-0 w-5 h-5  ${i === 1 ? "-ml-4" : ""}
          ${editorTheme == "light" ? "text-gray-700" : "text-gray-50"}`}
        />
      ))}
    </div>
  );
  function handleHintClick() {
    if (isHintLoading || revealedHintsCount >= (question?.hint?.length || 0))
      return;
    setShowHintSection(true);
    setRevealedHintsCount((prev) => prev + 1);
    setIsHintLoading(true);
    setProgress(0);

    let currentProgress = 0;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      currentProgress += 2; // 2% every 100ms = 5s
      setProgress(currentProgress);

      if (currentProgress >= 100) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsHintLoading(false);
        setProgress(0);
      }
    }, 100);
  }
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (showHintSection) {
      const el = document.getElementById("hints-section");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [showHintSection]);
  useEffect(() => {
    if (revealedHintsCount > 0) {
      const index = revealedHintsCount - 1;
      const el = document.getElementById(`hint-${index}`);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100); // small delay to ensure rendering
      }
    }
  }, [revealedHintsCount]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-x-hidden">
      <div
        ref={containerRef}
        style={
          {
            "--question-width": `${questionWidth}%`,
            ...(availableViewportHeight
              ? {
                  "--coding-panel-height": `${availableViewportHeight}px`
                }
              : {})
          } as CSSProperties
        }
        className="flex w-full min-h-0 flex-col rounded-2xl dark:bg-[#1E1E1E] lg:h-[var(--coding-panel-height)] lg:max-h-[var(--coding-panel-height)] lg:flex-row lg:overflow-hidden"
      >
        {/* Left */}
        {!editorFullScreen && (
          <div
            data-tour="acq-question"
            className="min-w-0 w-full shrink-0 px-4 py-6 border-b border-secondary/10 dark:border-0 md:px-5 md:py-8 lg:w-[var(--question-width)] lg:min-w-[18%] lg:max-w-[60%] lg:flex-[0_0_var(--question-width)] lg:self-stretch lg:overflow-hidden lg:hover:overflow-y-auto lg:thin-scrollbar lg:border lg:border-secondary/10 lg:border-r-0 lg:rounded-l-2xl lg:rounded-r-none"
          >
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex-1">
                <QuestionDisplay
                  question={question}
                  sectionId={sectionId}
                  totalQuestionsInSection={totalQuestionsInSection}
                />

                {sampleTestCases && sampleTestCases?.length > 0 && (
                  <TestCaseListing sampleTestCases={sampleTestCases} />
                )}

                <HintAndExplanation
                  question={question}
                  revealedHintsCount={revealedHintsCount}
                  showHintSection={showHintSection}
                />
              </div>

              {/* Buttons */}
              <div className="mt-6 border-t border-secondary/10 pt-4 dark:border-white/10 lg:mt-auto lg:border-t-0 lg:p-2 lg:sticky lg:bottom-[-28px] lg:bg-white lg:dark:bg-[#1E1E1E]">
                <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-4 lg:flex-nowrap lg:gap-5">
                  <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 md:w-auto md:max-w-[55%] md:flex-1 lg:max-w-none lg:flex-initial">
                    <Button
                      className="w-full text-[#AA6207] sm:w-auto"
                      variant="outlined"
                      buttonColor="secondary"
                      onClick={flagQuestionHandler}
                      disable={attemptingQuestion}
                    >
                      {questionFlagged
                        ? t("assessment.objective.unflag")
                        : t("assessment.objective.flag")}
                    </Button>
                    {((question?.hint && question?.hint.length > 0) ||
                      question?.codingexplanation) && (
                      <div className="flex w-full justify-start sm:w-auto sm:justify-start md:ml-0">
                        {question?.hint &&
                          question?.hint.length > 0 &&
                          (() => {
                            const hints = question.hint;
                            const hasHints = hints.length > 0;
                            const allHintsShown =
                              revealedHintsCount >= hints.length;

                            const getTooltipTitle = () => {
                              if (!hasHints) return "";
                              if (isHintLoading)
                                return t(
                                  "assessment.objective.hintWaitTooltip"
                                );
                              if (allHintsShown)
                                return t(
                                  "assessment.objective.noHintsPendingTooltip"
                                );
                              return "";
                            };

                            const buttonElement = (
                              <Button
                                onClick={handleHintClick}
                                disabled={isHintLoading || allHintsShown}
                                variant="outlined"
                                buttonColor="secondary"
                                size="md"
                                className="relative w-full overflow-hidden p-2.5 transition-all duration-300 text-[#FBBF24] sm:w-auto"
                              >
                                {/* Progress Bar Overlay */}
                                {isHintLoading && (
                                  <div
                                    className="absolute top-0 left-0 h-full bg-secondary-bg dark:bg-secondary/20 text-secondary/80 transition-all ease-linear"
                                    style={{
                                      width: `${progress}%`,
                                      zIndex: 1
                                    }}
                                  />
                                )}

                                {/* Button Content */}
                                <span className="relative z-10 flex items-center space-x-2">
                                  <span>
                                    {t("assessment.subjective.showHint")}
                                  </span>
                                </span>
                              </Button>
                            );

                            return (
                              <div className="relative w-full sm:w-fit">
                                {getTooltipTitle() ? (
                                  <Tooltip
                                    message={getTooltipTitle()}
                                    position="bottom-16"
                                  >
                                    <div>{buttonElement}</div>
                                  </Tooltip>
                                ) : (
                                  buttonElement
                                )}
                              </div>
                            );
                          })()}
                      </div>
                    )}
                  </div>

                  <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-between md:w-auto md:flex-1 md:justify-end lg:flex-initial lg:items-center lg:gap-5">
                    {question.orderInSection > 1 && (
                      <Button
                        className="w-full sm:w-auto"
                        buttonColor="secondary"
                        variant="outlined"
                        onClick={() => changeActiveQuestion("previous")}
                        disable={attemptingQuestion}
                      >
                        {t("assessment.objective.previous")}
                      </Button>
                    )}
                    {!isLastQuestionInTheSection && (
                      <Button
                        className="w-full sm:w-auto"
                        buttonColor="primary"
                        variant="contained"
                        onClick={() => next()}
                        disable={attemptingQuestion}
                      >
                        {t("assessment.objective.next")}
                      </Button>
                    )}
                    {isLastQuestionInTheSection && (
                      <Button
                        className="w-full sm:w-auto"
                        buttonColor="primary"
                        variant="contained"
                        onClick={() => {
                          next();
                        }}
                        disable={attemptingQuestion}
                      >
                        {t("assessment.objective.viewSectionSummary")}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Right - compiler */}
        <div className="relative flex w-full min-w-0 flex-1 lg:min-h-0 lg:w-[calc(100%-var(--question-width))]">
          {/* Resize handle*/}
          {!editorFullScreen && (
            <div
              className={`hidden lg:flex w-3 h-full items-center justify-center cursor-col-resize
                ${
                  editorTheme == "light"
                    ? "bg-gray-50 hover:bg-gray-200"
                    : "bg-gray-600 hover:bg-gray-700"
                }`}
              onMouseDown={startDrag}
            >
              <div className="flex flex-col items-center">
                <EllipsisRow />
                <div className="-mt-1">
                  <EllipsisRow />
                </div>
              </div>
            </div>
          )}

          {/* Editor */}
          <div className="w-full min-w-0 min-h-[22rem] md:min-h-[28rem] lg:min-h-0 lg:rounded-r-2xl">
            <AssessmentCodeEditor
              candidateResponseForQuestion={candidateResponseForQuestion}
              saveCodeInStoreHandler={saveCodeInStoreHandler}
              activeQuestionId={String(question.id)}
              questionData={question}
              editorTheme={editorTheme}
              setEditorTheme={setEditorTheme}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentCodingQuestion;
